import { useEffect, useState } from 'react';
import { Alert, Button, Modal, Select, Space, Typography } from 'antd';
import { space } from '@viq-admin/ui';
import type { BatchMoveApi, MoveDestination, MoveLeadsResult } from './types';

interface Props {
  open: boolean;
  api: BatchMoveApi;
  leadIds: string[];
  currentConferenceId: string;
  currentConferenceName: string;
  onClose: () => void;
  onMoved: (result: MoveLeadsResult) => void;
}

const CONSEQUENCE_COPY =
  'Discovery answers were given to this event’s questions, so they stay recorded here and appear on each lead under this event’s name. Nothing is deleted, and you can undo the move by moving the leads back.';

const EXPORT_NOTE =
  'Moved leads keep their original scan date. If the new event was already exported after that date, they appear under “Updated since last export”, not “Net new since last export”.';

/** The move endpoints' per-call ceiling (both planes). */
const MAX_LEADS_PER_MOVE = 200;

/** The CRM warning, singular/plural aware. */
function crmSentCopy(count: number, sourceName: string): string {
  const one = count === 1;
  return (
    `${count} of the selected leads ${one ? 'was' : 'were'} already sent to your CRM. ` +
    `${one ? 'It stays' : 'They stay'} with ${sourceName} there. ` +
    `To add ${one ? 'it' : 'them'} to the new event in your CRM, open that event and send ${one ? 'it' : 'them'} from its Send tab.`
  );
}

/**
 * Lead-move dialog, shared by the super-admin console and (for companies with
 * the lead-move flag) the customer portal. Moves mis-scanned leads out of
 * `currentConferenceId` into another conference belonging to the SAME client.
 *
 * The destination list comes from `api.listDestinations(api.companyId)`,
 * which is scoped server-side (an INNER JOIN on company_conferences) — this
 * component does NO client-side company filtering. The only filtering done
 * here is excluding the conference the leads are already in.
 *
 * Everything plane-specific arrives through `api`; `previewMove` (the CRM
 * warning) is wired by the portal only.
 */
export function MoveLeadsDialog({
  open,
  api,
  leadIds,
  currentConferenceId,
  currentConferenceName,
  onClose,
  onMoved,
}: Props) {
  const [destinations, setDestinations] = useState<MoveDestination[]>([]);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [destinationId, setDestinationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [crmSentCount, setCrmSentCount] = useState(0);

  // Reset + fetch destinations every time the dialog opens.
  useEffect(() => {
    if (!open) return undefined;

    setDestinationId(null);
    setMoveError(null);
    setDestinations([]);
    setFetchError(null);
    setCrmSentCount(0);

    const { companyId } = api;
    if (!companyId) {
      // The parent shouldn't render the dialog without a company, but guard
      // instead of crashing: fail soft with an explanatory error.
      setFetchError('Choose a company first to see the events you can move leads to.');
      return undefined;
    }

    let cancelled = false;
    setLoadingDestinations(true);
    api
      .listDestinations(companyId)
      .then((rows) => {
        if (!cancelled) setDestinations(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setFetchError((err as { message?: string }).message ?? 'Couldn’t load the events you can move to.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDestinations(false);
      });

    // Portal only. A failed preview is silent: the warning is advisory, and
    // the move itself is validated server-side. Skipped over the cap: the
    // server would refuse the selection anyway.
    if (leadIds.length <= MAX_LEADS_PER_MOVE) api.previewMove?.(leadIds)
      .then((p) => {
        if (!cancelled) setCrmSentCount(p.crmSentCount);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [open, api, leadIds]);

  const options = destinations
    .filter((d) => d.id !== currentConferenceId)
    .map((d) => ({ value: d.id, label: d.name }));

  async function onMove() {
    if (!destinationId) return;
    setBusy(true);
    setMoveError(null);
    try {
      const result = await api.moveLeads(leadIds, destinationId);
      onMoved(result);
    } catch (err) {
      setMoveError((err as { message?: string }).message ?? 'Couldn’t move the leads.');
    } finally {
      setBusy(false);
    }
  }

  const leadWord = leadIds.length === 1 ? 'lead' : 'leads';
  // The table keeps selections across "Load more", so a selection can exceed
  // what one call accepts. Say so rather than surface a bare 422.
  const overLimit = leadIds.length > MAX_LEADS_PER_MOVE;

  return (
    <Modal
      open={open}
      title={`Move ${leadIds.length} ${leadWord}`}
      onCancel={onClose}
      width={520}
      footer={null}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text>
          Move {leadIds.length} {leadWord} out of <strong>{currentConferenceName}</strong> into a
          different event.
        </Typography.Text>

        <Alert type="warning" showIcon message={CONSEQUENCE_COPY} />
        {crmSentCount > 0 && (
          <Alert type="warning" showIcon message={crmSentCopy(crmSentCount, currentConferenceName)} />
        )}
        <Typography.Text type="secondary">{EXPORT_NOTE}</Typography.Text>

        <div>
          <Typography.Text strong>Destination</Typography.Text>
          <Select
            aria-label="Destination"
            value={destinationId ?? undefined}
            onChange={(v) => setDestinationId(v)}
            placeholder="Choose an event"
            loading={loadingDestinations}
            style={{ width: '100%', marginTop: space.sm }}
            options={options}
            notFoundContent={loadingDestinations ? 'Loading…' : 'No other events to move to'}
          />
        </div>

        {overLimit && <Alert type="error" showIcon message={`Move up to ${MAX_LEADS_PER_MOVE} leads at a time.`} />}
        {fetchError && <Alert type="error" showIcon message={fetchError} />}
        {moveError && <Alert type="error" showIcon message={moveError} />}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.sm }}>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={onMove}
            loading={busy}
            disabled={!destinationId || busy || overLimit}
          >
            Move leads
          </Button>
        </div>
      </Space>
    </Modal>
  );
}
